import { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, LayoutDashboard, HandCoins, BarChart3, Landmark } from 'lucide-react';
import { useAuthStore } from '../../../stores/auth-store';
import ScrollToTop from '@/Components/ScrollToTop';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useCurrentUserWording } from '../../../hooks/useVendorWording';
import { useExternalUserDashboard } from '../hooks/useExternalUserDashboard';
import { DashboardContent } from '../components/DashboardContent';
import { ProvisionSection } from '../components/ProvisionSection';
import { DashboardFilters } from '../components/DashboardFilters';
import { RecapView } from '@/routes/admin/Vendors/VendorFinancial/RecapView';
import { SyntheseView } from '@/routes/admin/Vendors/VendorFinancial/SyntheseView';
import {
  useFinancialSummary,
  useFinancialCompiled,
  useContractsExport,
  useRecapExport,
  useSavExport,
} from '@/routes/admin/Vendors/VendorFinancial/useFinancialCompiled';
import { usePeriodDates, isCustomRangeIncomplete } from '../hooks/usePeriodDates';
import {
  DashboardPeriod,
  type ExternalDashboardFilters,
} from '../types/dashboard.types';

type ViewMode = 'dashboard' | 'provision' | 'details' | 'recap' | 'synthese';

type TabDef = { key: ViewMode; label: string; icon: typeof LayoutDashboard; title: string };

export const ExternalUserDashboardPage: React.FC = () => {
  const { currentUser } = useAuthStore();
  const wording = useCurrentUserWording();
  const societyId = currentUser?.societyId;
  const { data: effectiveConfig } = useEffectiveConfiguration(societyId);
  const isTransferPrice = effectiveConfig?.isTransferPrice || false;
  const hasExternalGuarantor = !!effectiveConfig?.guarantorId && effectiveConfig.guarantorId !== societyId;
  const isGuarantorTransfer = hasExternalGuarantor && isTransferPrice;

  const [filters, setFilters] = useState<ExternalDashboardFilters>({
    period: DashboardPeriod.LAST_12_MONTHS,
  });
  const [viewMode, setViewMode] = useState<ViewMode>('dashboard');
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');

  // Reset to dashboard tab when navigated via logo click
  useEffect(() => {
    if (searchParams.get('tab') === 'dashboard') {
      setViewMode('dashboard');
      window.scrollTo(0, 0);
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [retroDelayMonths, setRetroDelayMonths] = useState(0);
  const [paymentMode, setPaymentMode] = useState('');
  const [provisionWarrantyName, setProvisionWarrantyName] = useState<string | undefined>();

  // Dashboard data
  const { data, isLoading: dashboardLoading, error, refetch } = useExternalUserDashboard(
    currentUser!.id,
    filters,
    provisionWarrantyName,
  );

  // Préserver les options de garantie quand aucun filtre warranty n'est actif
  const warrantyOptionsRef = useRef<{ name: string; duration: number }[]>([]);
  if (!provisionWarrantyName && data?.warrantyFormulas?.length) {
    warrantyOptionsRef.current = data.warrantyFormulas.map((f: any) => ({ name: f.formulaName, duration: f.duration }));
  }

  const { hasPermission } = useAuthStore();

  // Tabs dynamiques (provision conditionnel + permissions)
  const tabs = useMemo<TabDef[]>(() => {
    const base: TabDef[] = [
      { key: 'dashboard', label: 'Tableau de bord général', icon: LayoutDashboard, title: 'Tableau de bord général' },
    ];
    if (!isGuarantorTransfer && data?.provisionEnabled && hasPermission('dashboard.view_provision')) {
      base.push({ key: 'provision', label: 'Suivi de la provision', icon: Landmark, title: 'Suivi de la provision' });
    }
    if (!isGuarantorTransfer && hasPermission('dashboard.view_encaissements')) {
      base.push({ key: 'recap', label: 'Suivi des encaissements', icon: HandCoins, title: 'Suivi des encaissements' });
    }
    if (!isGuarantorTransfer && effectiveConfig?.financialSynthesis && hasPermission('dashboard.view_synthese')) {
      base.push({ key: 'synthese', label: 'Synthèse financière', icon: BarChart3, title: 'Synthèse financière' });
    }
    return base;
  }, [data?.provisionEnabled, effectiveConfig?.financialSynthesis, hasPermission, isGuarantorTransfer]);

  // Period dates for financial tabs
  const periodDates = usePeriodDates(filters);

  // Période personnalisée en cours de saisie : on suspend les requêtes tant que
  // les deux bornes ne sont pas posées (sinon l'API renvoie tout l'historique).
  const rangeIncomplete = isCustomRangeIncomplete(filters);

  // Advanced filters (server-side)
  const [advancedFilters] = useState({
    catalog: 'all',
    sold: 'all',
    stripe: 'all',
    retro: 'all',
    refact: 'all',
  });


  // Financial data (only when society exists and tab is active)
  // Pagination du tableau contrats
  const [contractsPage, setContractsPage] = useState(0);
  const contractsLimit = 50;

  const financialQuery = useFinancialSummary(
    societyId || '',
    {
      dateFrom: periodDates.dateFrom,
      dateTo: periodDates.dateTo,
      search: search || undefined,
      paymentMode: paymentMode || undefined,
      storeId: filters.storeId,
      filterUserId: filters.filterUserId,
      page: contractsPage,
      limit: contractsLimit,
      catalogFilter: advancedFilters.catalog,
      soldFilter: advancedFilters.sold,
      stripeFilter: advancedFilters.stripe,
      excludeFree: viewMode === 'recap' ? true : undefined,
    },
    { enabled: !!societyId && !rangeIncomplete },
  );

  const compiledQuery = useFinancialCompiled(
    societyId || '',
    {
      dateFrom: periodDates.dateFrom,
      dateTo: periodDates.dateTo,
      storeId: filters.storeId,
      filterUserId: filters.filterUserId,
      retroDelayMonths,
    },
    { enabled: !!societyId && viewMode === 'synthese' && !rangeIncomplete },
  );

  const summary = financialQuery.data?.summary;
  const contracts = financialQuery.data?.contracts || [];

  // Export data hooks
  const contractsExportQuery = useContractsExport(
    societyId || '',
    { dateFrom: periodDates.dateFrom, dateTo: periodDates.dateTo, storeId: filters.storeId, filterUserId: filters.filterUserId },
    { enabled: false },
  );

  const savExportQuery = useSavExport(
    societyId || '',
    { dateFrom: periodDates.dateFrom, dateTo: periodDates.dateTo, storeId: filters.storeId, filterUserId: filters.filterUserId },
    { enabled: false },
  );
  const recapExportQuery = useRecapExport(
    societyId || '',
    {
      dateFrom: periodDates.dateFrom,
      dateTo: periodDates.dateTo,
      search: search || undefined,
      paymentMode: paymentMode || undefined,
      storeId: filters.storeId,
      filterUserId: filters.filterUserId,
      catalogFilter: advancedFilters.catalog,
      soldFilter: advancedFilters.sold,
      stripeFilter: advancedFilters.stripe,
      excludeFree: true,
    },
    { enabled: false },
  );
  const syntheseExportQuery = useRecapExport(
    societyId || '',
    {
      dateFrom: periodDates.dateFrom,
      dateTo: periodDates.dateTo,
      search: search || undefined,
      paymentMode: paymentMode || undefined,
      storeId: filters.storeId,
      filterUserId: filters.filterUserId,
      catalogFilter: advancedFilters.catalog,
      soldFilter: advancedFilters.sold,
      stripeFilter: advancedFilters.stripe,
    },
    { enabled: false },
  );

  // Contrats et totaux viennent du serveur (paginés + filtrés côté serveur)
  const filteredContracts = contracts;
  const totals = financialQuery.data?.totals || {
    count: 0, sellingPrice: 0, amountReceived: 0, amountRemaining: 0,
    vendorExpectedAmount: 0, vendorReceivedAmount: 0, vendorRemainingAmount: 0,
    platformFees: 0, remainingFees: 0,
  };
  const pagination = financialQuery.data?.pagination || { page: 0, limit: contractsLimit, totalCount: 0, totalPages: 0 };

  const hasChildren = (data?.stores || []).length > 1;
  const vendorName = currentUser?.firstname ? `${currentUser.firstname} ${currentUser.lastname}` : 'Vendeur';

  // Export Suivi des encaissements
  const [exportingRecap, setExportingRecap] = useState(false);
  const exportRecapCsv = async () => {
    setExportingRecap(true);
    try {
      const result = await recapExportQuery.refetch();
      const allContracts = result.data?.contracts || [];
      if (allContracts.length === 0) { setExportingRecap(false); return; }

      const formatCentimes = (v: number | null | undefined): string =>
        v === null || v === undefined ? '' : (v / 100).toFixed(2).replace('.', ',');
      const formatDate = (d: string | Date | null | undefined): string =>
        d ? new Date(d).toLocaleDateString('fr-FR') : '';
      const escapeCsv = (value: string): string =>
        value.includes(';') || value.includes('"') || value.includes('\n')
          ? '"' + value.replace(/"/g, '""') + '"' : value;

      const columns: Array<{ header: string; getValue: (c: any) => string }> = [
        { header: 'N° Contrat', getValue: (c) => c.label },
        { header: 'Client', getValue: (c) => `${c.beneficiary?.firstName || ''} ${c.beneficiary?.lastName || ''}`.trim() },
        { header: 'Statut', getValue: (c) => c.statusLabel },
        { header: 'Date', getValue: (c) => formatDate(c.dateCreation) },
        { header: 'Société', getValue: (c) => c.companyName },
        { header: 'Garantie', getValue: (c) => `${c.warrantyCode || ''} ${c.warrantyName || ''}`.trim() },
        { header: 'Mode', getValue: (c) => c.paymentMode },
        { header: 'Bénéficiaire Total (€)', getValue: (c) => formatCentimes(c.sellingPrice) },
        { header: 'Bénéficiaire Payé (€)', getValue: (c) => formatCentimes(c.amountReceived) },
        { header: 'Bénéficiaire Restant (€)', getValue: (c) => formatCentimes(c.amountRemaining) },
        { header: 'Société À recevoir (€)', getValue: (c) => formatCentimes(c.vendorExpectedAmount) },
        { header: 'Société Reçu (€)', getValue: (c) => formatCentimes(c.vendorReceivedAmount) },
        { header: 'Société Restant (€)', getValue: (c) => formatCentimes(c.vendorRemainingAmount) },
        { header: 'Frais Total (€)', getValue: (c) => formatCentimes((c.platformFees || 0) + (c.remainingFees || 0)) },
        { header: 'Frais Payés (€)', getValue: (c) => formatCentimes(c.platformFees) },
        { header: 'Frais Restants (€)', getValue: (c) => formatCentimes(c.remainingFees) },
      ];

      const headerLine = columns.map(col => escapeCsv(col.header)).join(';');
      const dataLines = allContracts.map((contract: any) =>
        columns.map(col => escapeCsv(col.getValue(contract))).join(';')
      );
      const csvContent = '\uFEFF' + [headerLine, ...dataLines].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `suivi-encaissements-${vendorName.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExportingRecap(false);
    }
  };

  const isLoading =
    viewMode === 'dashboard' ? dashboardLoading :
    viewMode === 'synthese' ? compiledQuery.isLoading :
    financialQuery.isLoading;

  // Available stores from dashboard data
  const availableStores = data?.stores || [];

  // Export CSV
  const [, setExporting] = useState(false);
  const exportToCsv = async () => {
    setExporting(true);
    try {
    const { data: exportData } = await syntheseExportQuery.refetch();
    const allContracts = exportData?.contracts || [];
    if (allContracts.length === 0) { setExporting(false); return; }

    const formatCentimes = (centimes: number | null | undefined): string => {
      if (centimes === null || centimes === undefined) return '';
      return (centimes / 100).toFixed(2).replace('.', ',');
    };
    const formatEuros = (euros: number | null | undefined): string => {
      if (euros === null || euros === undefined) return '';
      return Number(euros).toFixed(2).replace('.', ',');
    };
    const formatDate = (d: string | Date | null | undefined): string => {
      if (!d) return '';
      return new Date(d).toLocaleDateString('fr-FR');
    };
    const columns: Array<{ header: string; getValue: (c: any) => string }> = [
      { header: 'Contrat', getValue: (c) => c.label },
      { header: 'Statut', getValue: (c) => c.statusLabel },
      { header: 'Date création', getValue: (c) => formatDate(c.rawContract?.dateCreation) },
      { header: 'Date souscription', getValue: (c) => formatDate(c.rawContract?.dateSouscription) },
      { header: 'Date fin', getValue: (c) => formatDate(c.rawContract?.dateEnd) },
      { header: 'Bénéficiaire', getValue: (c) => `${c.beneficiary?.firstName || ''} ${c.beneficiary?.lastName || ''}`.trim() },
      { header: 'Email', getValue: (c) => c.beneficiary?.email || '' },
      { header: 'Société', getValue: (c) => c.companyName || '' },
      { header: 'Garantie', getValue: (c) => c.warrantyName || '' },
      { header: 'Mode paiement', getValue: (c) => c.paymentMode || '' },
      { header: 'Prix vente TTC (€)', getValue: (c) => formatCentimes(c.sellingPrice) },
      { header: 'Payé TTC (€)', getValue: (c) => formatCentimes(c.amountReceived) },
      { header: 'Reste TTC (€)', getValue: (c) => formatCentimes(c.amountRemaining) },
      { header: 'Société à recevoir (€)', getValue: (c) => formatCentimes(c.vendorExpectedAmount) },
      { header: 'Société reçu (€)', getValue: (c) => formatCentimes(c.vendorReceivedAmount) },
      { header: 'Société reste (€)', getValue: (c) => formatCentimes(c.vendorRemainingAmount) },
      { header: 'Frais payés (€)', getValue: (c) => formatCentimes(c.platformFees) },
      { header: 'Frais restants (€)', getValue: (c) => formatCentimes(c.remainingFees) },
      { header: 'Prix catalogue (€)', getValue: (c) => formatEuros(c.rawWarranty?.sellingPrice) },
      ...(isTransferPrice ? [
        { header: 'Rétrocession (€)', getValue: (c: any) => formatEuros(c.warrantyTransferPrice) },
        { header: 'Refacturation (€)', getValue: (c: any) => formatEuros(c.warrantyMemberTransferPrice) },
      ] : []),
      { header: 'SAV Bénéficiaire (€)', getValue: (c) => formatCentimes(c.savAmountBeneficiary) },
      { header: 'SAV Vendeur (€)', getValue: (c) => formatCentimes(c.savAmountSeller) },
      { header: 'SAV Commercial (€)', getValue: (c) => formatCentimes(c.savAmountSellerCommercial) },
      { header: 'SAV Total (€)', getValue: (c) => formatCentimes(c.savTotalAmount) },
    ];

    const escapeCsv = (value: string): string => {
      if (value.includes(';') || value.includes('"') || value.includes('\n')) {
        return '"' + value.replace(/"/g, '""') + '"';
      }
      return value;
    };

    const headerLine = columns.map(col => escapeCsv(col.header)).join(';');
    const dataLines = allContracts.map((contract: any) =>
      columns.map(col => escapeCsv(col.getValue(contract))).join(';'),
    );
    const csvContent = '\uFEFF' + [headerLine, ...dataLines].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `export-financier-${vendorName.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  // Export contrats CSV
  const [exportingContracts, setExportingContracts] = useState(false);
  const exportContractsCsv = async () => {
    setExportingContracts(true);
    try {
      const { data: rows } = await contractsExportQuery.refetch();
      if (!rows || rows.length === 0) { setExportingContracts(false); return; }

      const formatDate = (d: string | Date | null | undefined): string => {
        if (!d) return '';
        return new Date(d).toLocaleDateString('fr-FR');
      };
      const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
      const formatEuros = (v: unknown): string => (v === null || v === undefined ? '' : Number(v).toFixed(2).replace('.', ','));

      const columns = [
        { header: 'N° contrat', getValue: (c: any) => str(c.label) },
        { header: 'Statut', getValue: (c: any) => str(c.status) },
        { header: 'Date création', getValue: (c: any) => formatDate(c.dateCreation) },
        { header: 'Date souscription', getValue: (c: any) => formatDate(c.dateSouscription) },
        { header: 'Date fin', getValue: (c: any) => formatDate(c.dateEnd) },
        { header: 'Durée (mois)', getValue: (c: any) => str(c.duration) },
        { header: 'Société', getValue: (c: any) => str(c.societyName) },
        { header: 'Garantie', getValue: (c: any) => str(c.warrantyName) },
        { header: 'Prix garantie (€)', getValue: (c: any) => formatEuros(c.warrantyPrice) },
        ...(isTransferPrice ? [
          { header: 'Rétrocession (€)', getValue: (c: any) => formatEuros(c.warrantyTransferPrice) },
          { header: 'Refacturation (€)', getValue: (c: any) => formatEuros(c.warrantyMemberTransferPrice) },
        ] : []),
        { header: 'Mode paiement', getValue: (c: any) => str(c.paymentMode) },
        { header: 'Bénéficiaire prénom', getValue: (c: any) => str(c.beneficiaryFirstName) },
        { header: 'Bénéficiaire nom', getValue: (c: any) => str(c.beneficiaryLastName) },
        { header: 'Immatriculation', getValue: (c: any) => str(c.registration) },
        { header: 'Mise en circulation', getValue: (c: any) => str(c.entryIntoService) },
        { header: 'Marque', getValue: (c: any) => str(c.brand) },
        { header: 'Modèle', getValue: (c: any) => str(c.model) },
        { header: 'Finitions', getValue: (c: any) => str(c.finitions) },
        { header: 'Énergie', getValue: (c: any) => str(c.energy) },
        { header: 'Kilométrage', getValue: (c: any) => str(c.mileage) },
        { header: 'Prix véhicule (€)', getValue: (c: any) => formatEuros(c.vehiclePrice) },
      ];

      const escapeCsv = (value: string): string => {
        if (value.includes(';') || value.includes('"') || value.includes('\n')) return '"' + value.replace(/"/g, '""') + '"';
        return value;
      };

      const headerLine = columns.map(col => escapeCsv(col.header)).join(';');
      const dataLines = rows.map((row: any) => columns.map(col => escapeCsv(col.getValue(row))).join(';'));
      const csvContent = '\uFEFF' + [headerLine, ...dataLines].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = csvUrl;
      link.download = `export-contrats-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(csvUrl);
    } finally {
      setExportingContracts(false);
    }
  };

  // Export SAV CSV
  const [exportingSav, setExportingSav] = useState(false);
  const exportSavCsv = async () => {
    setExportingSav(true);
    try {
      const { data: rows } = await savExportQuery.refetch();
      if (!rows || rows.length === 0) { setExportingSav(false); return; }

      const formatDate = (d: string | Date | null | undefined): string => {
        if (!d) return '';
        return new Date(d).toLocaleDateString('fr-FR');
      };
      const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
      const formatEuros = (v: unknown): string => (v === null || v === undefined ? '' : Number(v).toFixed(2).replace('.', ','));

      const columns = [
        { header: 'ID SAV', getValue: (c: any) => str(c.savId) },
        { header: 'Réf SAV', getValue: (c: any) => str(c.uniqueId) },
        { header: 'Statut', getValue: (c: any) => str(c.status) },
        { header: 'Date déclaration', getValue: (c: any) => formatDate(c.dateDeclaration) },
        { header: 'Date de la panne', getValue: (c: any) => formatDate(c.dateSinistre) },
        { header: 'Date création', getValue: (c: any) => formatDate(c.dateCreation) },
        { header: 'Date de signature garant', getValue: (c: any) => formatDate(c.dateSignatureGarant) },
        { header: 'Description', getValue: (c: any) => str(c.description) },
        { header: 'KM au sinistre', getValue: (c: any) => str(c.savMileage) },
        { header: 'Part bénéficiaire (€)', getValue: (c: any) => formatEuros(c.amountBeneficiary) },
        { header: 'Part vendeur garantie (€)', getValue: (c: any) => formatEuros(c.amountSeller) },
        { header: 'Part vendeur commercial (€)', getValue: (c: any) => formatEuros(c.amountSellerCommercial) },
        { header: 'Total (€)', getValue: (c: any) => formatEuros(c.amountTotal) },
        { header: 'N° contrat', getValue: (c: any) => str(c.contractLabel) },
        { header: 'Date souscription', getValue: (c: any) => formatDate(c.contractDateSouscription) },
        { header: 'Durée contrat (mois)', getValue: (c: any) => str(c.contractDuration) },
        { header: 'Société', getValue: (c: any) => str(c.societyName) },
        { header: 'Garantie', getValue: (c: any) => str(c.warrantyName) },
        { header: 'Bénéficiaire prénom', getValue: (c: any) => str(c.beneficiaryFirstName) },
        { header: 'Bénéficiaire nom', getValue: (c: any) => str(c.beneficiaryLastName) },
        { header: 'Immatriculation', getValue: (c: any) => str(c.registration) },
        { header: 'Mise en circulation', getValue: (c: any) => str(c.entryIntoService) },
        { header: 'Marque', getValue: (c: any) => str(c.brand) },
        { header: 'Modèle', getValue: (c: any) => str(c.model) },
        { header: 'Finitions', getValue: (c: any) => str(c.finitions) },
        { header: 'Énergie', getValue: (c: any) => str(c.energy) },
        { header: 'Kilométrage', getValue: (c: any) => str(c.mileage) },
        { header: 'Prix véhicule (€)', getValue: (c: any) => formatEuros(c.vehiclePrice) },
      ];

      const escapeCsv = (value: string): string => {
        if (value.includes(';') || value.includes('"') || value.includes('\n')) return '"' + value.replace(/"/g, '""') + '"';
        return value;
      };

      const headerLine = columns.map(col => escapeCsv(col.header)).join(';');
      const dataLines = rows.map((row: any) => columns.map(col => escapeCsv(col.getValue(row))).join(';'));
      const csvContent = '\uFEFF' + [headerLine, ...dataLines].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = csvUrl;
      link.download = `export-sav-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(csvUrl);
    } finally {
      setExportingSav(false);
    }
  };

  if (error) {
    return (
      <div className="tw-pt-4">
        <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-6 tw-text-center">
          <p className="tw-text-red-600 tw-font-medium">
            Erreur lors du chargement du dashboard
          </p>
          <p className="tw-text-sm tw-text-red-500 tw-mt-2">
            {error instanceof Error ? error.message : 'Erreur inconnue'}
          </p>
          <button
            onClick={() => refetch()}
            className="tw-mt-4 tw-px-4 tw-py-2 tw-bg-red-600 tw-text-white tw-rounded-md hover:tw-bg-red-700 tw-transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-pt-4">
      {/* Filtres communs */}
      <DashboardFilters
        filters={filters}
        onFiltersChange={setFilters}
        stores={availableStores}
        availableUsers={data?.availableUsers || []}
      />

      {tabs.length > 1 && (
        <div className="tw-sticky tw-top-0 tw-z-30 tw-bg-gray-50 tw-mb-2 sm:tw-mb-3 tw--mx-4 tw-px-4 tw-py-1 sm:tw-py-1.5">
          <div className="tw-flex tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-100 tw-p-1 tw-overflow-x-auto tw-scrollbar-hide">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = viewMode === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setViewMode(tab.key)}
                  className={`tw-flex tw-flex-1 tw-items-center tw-justify-center tw-gap-1 sm:tw-gap-1.5 tw-px-2 sm:tw-px-3 tw-py-2 tw-rounded-md tw-text-xs sm:tw-text-sm tw-font-medium tw-transition-colors tw-whitespace-nowrap ${
                    isActive
                      ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm'
                      : 'tw-text-gray-600 hover:tw-text-gray-900'
                  }`}
                  title={tab.title}
                >
                  <Icon size={16} className="tw-shrink-0" />
                  <span className="tw-hidden sm:tw-inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Contenu */}
      {isLoading ? (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20">
          <Loader2 className="tw-w-12 tw-h-12 tw-text-affinicar-primary tw-animate-spin tw-mb-4" />
          <p className="tw-text-gray-600">Chargement de vos données...</p>
        </div>
      ) : viewMode === 'dashboard' ? (
        data ? (
          <DashboardContent
            data={data}
            filters={filters}
            onFiltersChange={setFilters}
            isLoading={dashboardLoading}
            wording={wording}
            hideFilters
            onExportContracts={exportContractsCsv}
            onExportSav={exportSavCsv}
            exportingContracts={exportingContracts}
            exportingSav={exportingSav}
            isGuarantorTransfer={isGuarantorTransfer}
          />
        ) : null
      ) : viewMode === 'provision' ? (
        data?.provisionReport ? (
          <ProvisionSection
            report={data.provisionReport}
            societyId={societyId!}
            filters={{ dateFrom: periodDates.dateFrom, dateTo: periodDates.dateTo, storeId: filters.storeId }}
            warrantyOptions={warrantyOptionsRef.current}
            selectedWarrantyName={provisionWarrantyName}
            onWarrantyChange={(name) => setProvisionWarrantyName(name || undefined)}
          />
        ) : (
          <p className="tw-text-center tw-text-gray-500 tw-py-12">Aucune donnée disponible</p>
        )
      ) : viewMode === 'recap' ? (
        summary ? (
          <RecapView
            summary={summary}
            filteredContracts={filteredContracts}
            totals={totals}
            hasChildren={hasChildren}
            societyId={data?.societyId || ''}
            vendorName={vendorName}
            pagination={pagination}
            onPageChange={setContractsPage}
            onExport={exportRecapCsv}
            exporting={exportingRecap}
            search={search}
            onSearchChange={setSearch}
            paymentMode={paymentMode}
            onPaymentModeChange={setPaymentMode}
            monthlyPayment={effectiveConfig?.monthlyPayment}
            onlinePayment={effectiveConfig?.onlinePayment}
          />
        ) : (
          <p className="tw-text-center tw-text-gray-500 tw-py-12">Aucune donnée disponible</p>
        )
      ) : viewMode === 'synthese' ? (
        compiledQuery.data ? (
          <SyntheseView
            data={compiledQuery.data}
            isTransferPrice={isTransferPrice}
            onExport={exportToCsv}
            retroDelayMonths={retroDelayMonths}
            onRetroDelayChange={setRetroDelayMonths}
          />
        ) : (
          <p className="tw-text-center tw-text-gray-500 tw-py-12">Aucune donnée disponible</p>
        )
      ) : null}
      <ScrollToTop />
    </div>
  );
};
